import { LogOut, Shield, Trash2 } from 'lucide-react';
import { Modal, Pill, ConfirmDialog } from '@/design-system';
import { cn } from '@/shared/cn';
import { ADMIN_ACTION_BUTTON, BUTTON_PRIMARY, FIELD_INPUT } from '@/shared/ui-class';
import type { AdminUserSummary } from '@/versions/v1/shared/admin-seed';
import { CURRENT_USER_ID, formatAdminDate } from './admin-shared';
import { statusLabel, statusPillTone } from './UsersScene.transform';

export type AdminUserDetail = AdminUserSummary & {
  threadCount: number;
  projectCount: number;
  updatedAt?: number | null;
  blockedAt?: number | null;
  blockedReason?: string | null;
};

type UserDetailDrawerProps = {
  user: AdminUserDetail | null;
  onClose: () => void;
  onSaveName: (name: string) => void;
  onBlock: () => void;
  onUnblock: () => void;
  onReturnToLobby: () => void;
  onToggleAdmin: () => void;
  onDelete: () => void;
  deleteOpen: boolean;
  onDeleteOpenChange: (open: boolean) => void;
};

export function UserDetailDrawer({
  user,
  onClose,
  onSaveName,
  onBlock,
  onUnblock,
  onReturnToLobby,
  onToggleAdmin,
  onDelete,
  deleteOpen,
  onDeleteOpenChange,
}: UserDetailDrawerProps) {
  if (!user) return null;

  const isSelf = user.id === CURRENT_USER_ID;

  return (
    <>
      <Modal
        open={user !== null}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title="User detail"
        placement="right"
      >
        <div className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted">
              Identity
            </h3>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-muted">Email</span>
              <input value={user.email} readOnly className={FIELD_INPUT} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs text-muted">Name</span>
              <input
                key={user.id}
                defaultValue={user.name ?? ''}
                className={FIELD_INPUT}
                onBlur={(event) => onSaveName(event.target.value)}
              />
            </label>
            <button type="button" onClick={() => onSaveName(user.name ?? '')} className={BUTTON_PRIMARY}>
              Save name
            </button>
          </section>

          <section className="space-y-2">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted">Status</h3>
            <div className="flex flex-wrap gap-2">
              <Pill tone={statusPillTone(user.status)}>{statusLabel(user.status)}</Pill>
              {user.platformRole === 'admin' ? <Pill tone="admin">admin</Pill> : null}
              {user.isEnvAdmin ? <Pill>ENV</Pill> : null}
            </div>
            {user.blockedAt ? (
              <p className="text-sm text-muted">
                Blocked {formatAdminDate(user.blockedAt)}
                {user.blockedReason ? ` — ${user.blockedReason}` : ''}
              </p>
            ) : null}
          </section>

          <section className="space-y-1 text-sm text-muted">
            <h3 className="text-xs font-medium uppercase tracking-wide text-muted">Activity</h3>
            <p>
              <span className="text-muted text-xs">Created:</span>{' '}
              {formatAdminDate(user.createdAt)}
            </p>
            {user.updatedAt ? (
              <p>
                <span className="text-muted text-xs">Updated:</span>{' '}
                {formatAdminDate(user.updatedAt)}
              </p>
            ) : null}
            <p>
              Threads: {user.threadCount} · Projects: {user.projectCount}
            </p>
          </section>

          <div className="flex flex-wrap gap-2 border-t border-[var(--glass-border)] pt-4">
            {user.status === 'blocked' ? (
              <button
                type="button"
                disabled={user.isEnvAdmin}
                onClick={onUnblock}
                className={ADMIN_ACTION_BUTTON}
              >
                Unblock
              </button>
            ) : (
              <button
                type="button"
                disabled={user.isEnvAdmin || isSelf}
                onClick={onBlock}
                className={cn(ADMIN_ACTION_BUTTON, 'text-red-600 dark:text-red-400')}
              >
                Block
              </button>
            )}
            <button
              type="button"
              disabled={user.isEnvAdmin || user.status === 'pending'}
              onClick={onReturnToLobby}
              className={ADMIN_ACTION_BUTTON}
            >
              <LogOut className="mr-1 inline h-3.5 w-3.5" />
              Return to lobby
            </button>
            <button
              type="button"
              disabled={user.isEnvAdmin}
              onClick={onToggleAdmin}
              className={ADMIN_ACTION_BUTTON}
            >
              <Shield className="mr-1 inline h-3.5 w-3.5" />
              {user.platformRole === 'admin' ? 'Remove admin' : 'Make admin'}
            </button>
            <button
              type="button"
              disabled={user.isEnvAdmin || isSelf}
              onClick={() => onDeleteOpenChange(true)}
              className={cn(ADMIN_ACTION_BUTTON, 'text-red-600 dark:text-red-400')}
            >
              <Trash2 className="mr-1 inline h-3.5 w-3.5" />
              Delete
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={onDeleteOpenChange}
        title="Delete user"
        description={
          <>
            <p>
              This permanently deletes <strong>{user.email}</strong> — approximately{' '}
              {user.threadCount} threads, plus memories, files, connectors, and skills.
            </p>
            {user.projectCount > 0 ? (
              <p className="mt-4 space-y-1.5">
                Projects are not deleted — this user owns {user.projectCount} project
                {user.projectCount === 1 ? '' : 's'}, so the delete will be refused until
                ownership is transferred to another account via the API.
              </p>
            ) : null}
            <p className="mt-4">
              This cannot be undone. <em>Prefer blocking if you may need this account again.</em>
            </p>
          </>
        }
        confirmLabel="Delete"
        tone="danger"
        onConfirm={onDelete}
      />
    </>
  );
}
